import { motion } from "framer-motion";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { amazonUrl, BOOKS, formatPrice } from "@/data/books";

export default function LondonSpotlight() {
  const book = BOOKS.find((b) => b.slug === "bailey-in-london");

  return (
    <section data-testid="london-spotlight" className="relative overflow-hidden bg-walnut text-cream">
      <div className="absolute w-[34rem] h-[34rem] rounded-full bg-terracotta/25 blur-[130px] -top-32 -left-24 pointer-events-none" />
      <div className="absolute w-[26rem] h-[26rem] rounded-full bg-sage/20 blur-[110px] bottom-0 right-0 pointer-events-none" />

      <svg
        data-testid="london-eye-decoration"
        viewBox="0 0 200 210"
        className="absolute bottom-0 right-2 sm:right-8 h-40 sm:h-56 w-auto opacity-90 pointer-events-none"
        aria-hidden="true"
      >
        <g className="wheel" style={{ animationDuration: "36s" }}>
          <circle cx="100" cy="90" r="56" fill="none" stroke="#4FA3E3" strokeWidth="6" />
          <circle cx="100" cy="90" r="46" fill="none" stroke="#BFE0F7" strokeWidth="2.5" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const rad = (a * Math.PI) / 180;
            const x2 = 100 + 56 * Math.cos(rad);
            const y2 = 90 + 56 * Math.sin(rad);
            return <line key={a} x1="100" y1="90" x2={x2} y2={y2} stroke="#BFE0F7" strokeWidth="2.5" />;
          })}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a, i) => {
            const rad = (a * Math.PI) / 180;
            const cx = 100 + 63 * Math.cos(rad);
            const cy = 90 + 63 * Math.sin(rad);
            const colors = ["#FFE9A8", "#A8D5A2", "#F5B8A6", "#BFE0F7"];
            return <circle key={a} cx={cx} cy={cy} r="9" fill={colors[i % 4]} stroke="#31435C" strokeWidth="2" />;
          })}
          <circle cx="100" cy="90" r="9" fill="#FFF4CC" stroke="#31435C" strokeWidth="2.5" />
        </g>
        <line x1="100" y1="99" x2="66" y2="192" stroke="#31435C" strokeWidth="6" strokeLinecap="round" />
        <line x1="100" y1="99" x2="134" y2="192" stroke="#31435C" strokeWidth="6" strokeLinecap="round" />
        <line x1="78" y1="150" x2="122" y2="150" stroke="#31435C" strokeWidth="4" strokeLinecap="round" />
        <rect x="52" y="190" width="96" height="9" rx="4.5" fill="#31435C" />
      </svg>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-2 gap-14 items-center relative z-10">
        <Reveal className="order-2 lg:order-1">
          <div className="relative inline-block">
            <motion.img
              src={book.cover}
              alt={`${book.title} — the newest Bailey adventure`}
              className="w-64 sm:w-80 rounded-xl shadow-[0_44px_80px_-28px_rgba(0,0,0,0.65)] animate-float-slow"
              whileHover={{ scale: 1.04, rotate: -1.5 }}
              transition={{ type: "spring", stiffness: 180, damping: 16 }}
            />
            <motion.span
              initial={{ scale: 0, rotate: -20 }}
              whileInView={{ scale: 1, rotate: -8 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.35 }}
              className="absolute -top-5 -right-5 sm:-right-8 bg-terracotta text-cream font-display font-extrabold text-xs sm:text-sm uppercase tracking-widest rounded-full px-5 py-3 shadow-[0_16px_30px_-10px_rgba(224,109,83,0.7)]"
            >
              Just released
            </motion.span>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow className="mb-4 !text-terracotta">Book 05 · Available now</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight leading-[1.06] text-4xl sm:text-5xl lg:text-6xl mb-6">
              Bailey in <span className="text-terracotta">London</span>
            </h2>
            <p className="max-w-lg text-sm sm:text-base text-cream/75 leading-relaxed mb-4">
              {book.description}
            </p>
            <p className="max-w-lg text-sm text-cream/55 leading-relaxed mb-8">
              {book.longDescription}
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex items-center gap-4 mb-8">
              <span className="font-display font-extrabold text-3xl sm:text-4xl">{formatPrice(book.price)}</span>
              <span className="text-xs text-cream/50">{book.ages} · {book.format}</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <a
                data-testid="london-buy-amazon"
                href={amazonUrl(book)}
                target="_blank"
                rel="noopener noreferrer"
                className="group rounded-full bg-terracotta text-cream pl-7 pr-6 py-4 font-display font-bold text-sm sm:text-base flex items-center gap-2 hover:bg-terracottadark transition-colors shadow-[0_18px_36px_-14px_rgba(224,109,83,0.6)]"
              >
                Buy on Amazon UK
                <ExternalLink size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <Link
                to={`/books/${book.slug}`}
                data-testid="london-discover"
                className="group rounded-full border-2 border-cream/70 text-cream px-7 py-[0.85rem] font-display font-bold text-sm sm:text-base flex items-center gap-2 hover:bg-cream hover:text-walnut transition-colors"
              >
                Discover the story
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <p className="mt-6 text-xs text-cream/45">Written by Damali Allison · Illustrated by Meni Etim</p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
